import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { CircularProgress } from '@mui/material';
import { jwtDecode } from 'jwt-decode'; // Ensure you're using jwt-decode correctly

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true); // Loading state for async check
    const navigate = useNavigate(); // For navigation

    // Check for token validity on component mount
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        console.log('Auth Token found:', token);

        if (token) {
            try {
                const decodedToken = jwtDecode(token); // Decode the JWT
                const isTokenExpired = decodedToken.exp * 1000 < Date.now(); // Check if token is expired

                if (isTokenExpired) {
                    console.log('Token expired');
                    localStorage.removeItem('authToken');
                    setIsAuthenticated(false);
                    navigate('/signin'); // Redirect to login
                } else {
                    axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
                    setIsAuthenticated(true);
                }
            } catch (error) {
                console.error('Invalid token:', error);
                localStorage.removeItem('authToken');
                setIsAuthenticated(false);
                navigate('/signin'); // Redirect to login
            }
        } else {
            setIsAuthenticated(false); // No token found
        }

        setLoading(false); // Authentication check is complete
    }, [navigate]);

    // Login function with token
    const login = (token) => {
        localStorage.setItem('authToken', token);
        axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
        setIsAuthenticated(true);
        navigate('/home');
    };

    // Logout function
    const logout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        axiosInstance.defaults.headers['Authorization'] = '';
        navigate('/signin');
    };

    // Authentication functions
    const sendWhatsAppAuth = async (phone) => {
        const response = await axiosInstance.post('/api/auth/wa/', { phone });
        return response.data;
    };

    const verifyWhatsAppAuth = async (phone, otp) => {
        const response = await axiosInstance.post('/api/auth/wa/verif', { phone, otp });
        return response.data;
    };

    const sendEmailAuth = async (email, password) => {
        const response = await axiosInstance.post('/api/auth/email', { email, password });
        return response.data;
    };

    const verifyEmailAuth = async (email, password, otp) => {
        const response = await axiosInstance.post('/api/auth/email/verif', { email, password, otp });
        return response.data;
    };

    const forgotPassword = async (username, type) => {
        const response = await axiosInstance.post('/api/auth/forgot', { username, type });
        return response.data;
    };

    const registerWithWhatsApp = async (phone) => {
        const response = await axiosInstance.post('/api/register/wa', { phone });
        return response.data;
    };

    const registerWithEmail = async (email, name, password, confirmPassword) => {
        const response = await axiosInstance.post('/api/register/email', { email, name, password, confirm_password: confirmPassword });
        return response.data;
    };

    const verifyWhatsAppRegistration = async (phone, otp) => {
        const response = await axiosInstance.post('/api/register/wa/verif', { phone, otp });
        return response.data;
    };

    const verifyEmailRegistration = async (name, email, password, otp) => {
        const response = await axiosInstance.post('/api/register/email/verif', { name, email, password, otp });
        return response.data;
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated, login, logout, loading,
            sendWhatsAppAuth, verifyWhatsAppAuth, sendEmailAuth, verifyEmailAuth,
            forgotPassword, registerWithWhatsApp, registerWithEmail,
            verifyWhatsAppRegistration, verifyEmailRegistration
        }}>
            {loading ? <CircularProgress /> : children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
